/* Typed wrappers for the proactive notification inbox + preferences.
 *
 *   GET  /tools/notifications/inbox          → InboxAlert[]
 *   GET  /tools/notifications/inbox/count    → { unread }
 *   POST /tools/notifications/{id}/read      → mark read
 *   POST /tools/notifications/{id}/dismiss   → dismiss
 *   GET  /tools/notifications/preferences    → NotificationPreferences
 *   POST /tools/notifications/preferences    → upsert
 *
 * These are SPA-direct calls (APIM injects X-User-OID). They are NOT agent
 * tools — only `record_alert` (the write that populates the inbox) is in the
 * tool registry. Bid-award notifications stay on the existing ingestion
 * /notifications/* flow and are merged into the inbox UI client-side.
 *
 * Vocabulary types live in schemas.ts (lockstep guard). */

import { ApiError } from './client';
import type { InboxAlert, NotificationPreferences, ToolResponse } from './schemas';

export type { InboxAlert, NotificationPreferences } from './schemas';

const APIM = import.meta.env.VITE_APIM_BASE_URL || 'http://localhost:7071';

async function callTool<T>(
  path: string,
  token: string | null,
  opts: { method?: 'GET' | 'POST'; body?: unknown } = {}
): Promise<ToolResponse<T>> {
  const method = opts.method ?? 'GET';
  const res = await fetch(`${APIM}${path}`, {
    method,
    headers: {
      ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
      ...(token && token !== 'mock-token' ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: method === 'GET' || opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });
  if (!res.ok) {
    let msg = res.statusText;
    let code: string | undefined;
    try {
      const err = await res.json();
      msg = err?.error?.message ?? err?.detail ?? msg;
      code = err?.error?.error_code ?? err?.error_code ?? err?.code ?? undefined;
    } catch {
      /* ignore */
    }
    throw new ApiError(msg, res.status, code);
  }
  return (await res.json()) as ToolResponse<T>;
}

export async function getInbox(
  token: string | null,
  params: { status?: string; limit?: number; offset?: number } = {}
): Promise<InboxAlert[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const wrap = await callTool<InboxAlert>(`/tools/notifications/inbox${suffix}`, token);
  return wrap.data ?? [];
}

/** Unread count for the bell badge. Never throws to the caller's UI —
 *  the bell is non-critical chrome; callers treat a failure as 0. */
export async function getInboxCount(token: string | null): Promise<number> {
  const wrap = await callTool<{ unread: number }>('/tools/notifications/inbox/count', token);
  return wrap.data?.[0]?.unread ?? 0;
}

export async function markAlertRead(token: string | null, alertId: number): Promise<void> {
  await callTool(`/tools/notifications/${alertId}/read`, token, { method: 'POST', body: {} });
}

export async function dismissAlert(token: string | null, alertId: number): Promise<void> {
  await callTool(`/tools/notifications/${alertId}/dismiss`, token, { method: 'POST', body: {} });
}

export async function getNotificationPreferences(
  token: string | null
): Promise<NotificationPreferences> {
  const wrap = await callTool<NotificationPreferences>('/tools/notifications/preferences', token);
  return (
    wrap.data?.[0] ?? {
      enabled: true,
      quiet_hours_start: null,
      quiet_hours_end: null,
      quiet_hours_tz: 'UTC',
      muted_project_ids: [],
    }
  );
}

export async function setNotificationPreferences(
  token: string | null,
  prefs: NotificationPreferences
): Promise<void> {
  await callTool('/tools/notifications/preferences', token, { method: 'POST', body: prefs });
}

/* ---------------- Bid-award notifications ----------------
 *
 * The bid-award flow lives in the ingestion service, but the browser CANNOT
 * call ingestion /notifications/* directly — that path is not exposed on APIM
 * (only /tools, /generate, /search, /ingestion, /platform, /foundry), so it
 * 404'd. So the inbox reads + triages pending bids through the tools-service
 * (on the /tools/* route), which serves an access-scoped read-through of
 * bid_notifications. Award execution still happens in the ingestion/bid flow. */

export interface PendingBidNotification {
  id: number;
  bid_id: number;
  status: string;
  trigger_type: string | null;
  suggested_procore_project_name: string | null;
  created_at: string;
  updated_at: string | null;
}

/** Pending bid-award notifications for the user. Best-effort: callers treat a
 *  failure as an empty list so the proactive inbox still renders. */
export async function getPendingBidNotifications(
  token: string | null
): Promise<PendingBidNotification[]> {
  const wrap = await callTool<PendingBidNotification>('/tools/notifications/bids', token);
  // The tools-service returns success:false at HTTP 200 on a backend read error;
  // callTool only throws on non-2xx, so surface the envelope failure ourselves
  // (callers .catch(() => []) — but a real error is now an error, not "empty").
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'Failed to load bid notifications', 200, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** Throw when the tools-service write returns success:false at HTTP 200 (e.g. a
 *  DB error, or the notification is no longer pending). Without this the void
 *  wrapper would treat a failed triage as success and the SPA's optimistic
 *  removal would stick while the bid stays pending. */
function throwIfFailed(wrap: { success: boolean; error?: string | null; error_code?: string | null }, verb: string): void {
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? `Could not ${verb} the bid notification`, 200, wrap.error_code ?? undefined);
  }
}

export async function confirmBidNotification(token: string | null, id: number): Promise<void> {
  const wrap = await callTool(`/tools/notifications/bids/${id}/confirm`, token, { method: 'POST', body: {} });
  throwIfFailed(wrap, 'confirm');
}

export async function dismissBidNotification(token: string | null, id: number): Promise<void> {
  const wrap = await callTool(`/tools/notifications/bids/${id}/dismiss`, token, { method: 'POST', body: {} });
  throwIfFailed(wrap, 'dismiss');
}
